import {Avatar, DatePicker, Input, Radio} from 'antd'
import {getId} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import {ResetButton, SubmitButton} from 'components/form/FormActionBar'
import Pure from 'components/Pure'
import {Formik} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo} from 'react'
import {compose, mapProps, pure, withProps} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {BooleanPicker} from "../../Exam/ExamContext";
import {createSelectValue, createValue} from "../../../../components/form/utils";
import {StatusTypes} from "../../appFieldTypes";
import Translate from "../../../../modules/local/Translate";
import LookupField from "../../../../components/LookupField";
import SlateEditor from "../../../SlateEditor";
import {ModalEditorToolbar} from "../../../SlateEditor/SlateEditor";
import {convertFromString, convertToString} from "../../../SlateEditor/functions";
import {SelectWidget} from "../../custom/ReactSelectComponents";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsShieldFillExclamation} from "react-icons/bs";
import {SaintsOutlineSvg} from "../../../../components/Svgs/SaintsPlaceholder";
import dayjs from "dayjs";
import {IoCalendarOutline} from "react-icons/io5";
import {joinPolicyOptions, privacyOptions} from "../../../../conts/privacyOptions";
import {
    firstLetterUppercase,
    lengthBetween, noDoubleSpaces,
    noLeadingTrailingSpaces, noNumber,
    noSpecialChars,
    required, runRules
} from "../../../../components/form/ValidateForm";

const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange}) => ({
                        showCount: true,
                        maxLength: 120,
                        name,
                        value,
                        onChange,
                        placeholder: translate('title'),
                    })),
                    pure
                )(Input),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    showCount: true,
                    maxLength: 500,
                    rows: 3,
                    placeholder: 'description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },

    status: {
        title: 'status',
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="custom-radio-button"
                        />
                    )
                },
            },
        ],
    },

    active: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1 md:col-span-2', 'col-span-1'],
        children: [
            {
                label: 'status',
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="w-full custom-radio-button"
                        />
                    )
                },
            },
            {
                name: 'active',
                label: 'active',
                component: createControlledFormField({
                    Component: BooleanPicker,
                }),
            },
        ],
    },

    privacy: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'privacy',
                name: 'privacy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('privacy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(privacyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
            {
                label: 'join policy',
                name: 'join_policy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('join_policy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(joinPolicyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
        ]
    },
    channel_post_permission: {
        title: 'posting permission',
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate("Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post"),
                    className: "p-2 border border-dashed border-primary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },

    department_group: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1', 'col-span-1 md:col-span-2'],
        // title: 'category',
        children: [
            {
                label: 'group number',
                name: 'group_nbr',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('group_nbr', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((e) => ({
                            value: e,
                            label: `${translate('group')} ${e}`,
                        })),
                    })),
                    pure
                )(SelectWidget),
            },
            {
                label: 'group name',
                name: 'department_group_title',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    const organization = _.get(values, 'organization', {})
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={
                                        values?.department_group_title
                                    }
                                    apiPath={`/lookup/department_group_title/gets?group_type=organization_department`}
                                    name={name}
                                    placeholder={t('choose or create')}
                                    handleChange={(e) => {
                                        onChange(
                                            createValue('department_group_title', e.target.value)
                                        )
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    about: {
        title: 'about',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
    patron_saint: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'holy name',
                name: 'patron_saint',
                component: ({name, value, onChange, form, ...restForm}) => {
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={form?.values?.patron_saint_name}
                                    selectedOption={form?.values?.patron_saint}
                                    apiPath={`/lookup/saint/get-by-types`}
                                    name={name}
                                    OptionWrapper={({children, ...props}) =>
                                        <div
                                            className="flex items-center gap-2 border border-color-50 hover:shadow-md rounded-lg relative" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => {
                                        return (
                                            <div key={i} className="flex items-center flex-1 gap-3">
                                                <Avatar
                                                    size={60}
                                                    src={o.avatar}
                                                    className="flex flex-center background-200 rounded-l-lg rounded-r-0"
                                                    icon={<SaintsOutlineSvg size={30}
                                                                            style={{color: '#dddddd'}}/>}
                                                />
                                                <div className="flex flex-col flex-1 max-lines-3">
                                                            <span
                                                                className="font-bold text-sm text-color-000 leading-tight">
                                                    {`${o.common_name || o.name} (${o.patron_saint_feast_day || o.feast_day})`}
                                                            </span>
                                                    {renderIf(
                                                        o?.description,
                                                        <div className="flex items-center">
                                                                <span
                                                                    className="text-xs text-color-400 italic max-lines-2 leading-snug">{o?.description}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }}
                                    handleChange={(e) => {
                                        form.setValues({
                                            ...form.values,
                                            patron_saint: e.target,
                                            patron_saint_name: e.target.common_name || e.target.value,
                                            patron_saint_feast_day: e.target.feast_day,
                                            patron_saint_id: e.target.id
                                        });
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
            {
                label: 'patron saint feast day',
                name: 'patron_saint_feast_day',
                component: compose(
                    withTranslate,
                    withProps(({value, onChange, translate}) => ({
                        onChange: (date, dateString) => {
                            onChange(createValue('patron_saint_feast_day', dateString))
                        },
                        value: !!value ? dayjs(value, "DD/MM") : null,
                        className: 'w-full',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        name: 'patron saint feast day',
                        placeholder: translate('patron saint feast day'),
                        format: 'DD/MM'
                    }))
                )(DatePicker),
            },
        ],
    },
}

const validateTitleOrDepartmentGroupTitle = (name, keyName, t) => {
    const maxLength = {
        'title': 120,
        'department_group_title': 180
    }
    if(keyName !== 'title' && !name) return null;
    const rules = [
        (keyName === 'title') ? required(t) : null,
        lengthBetween(1, maxLength[keyName], t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return { [keyName]: message }
}

const validateDescription = (description, t) => {
    const rules = [
        lengthBetween(1, 500, t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(description, rules)
    if (message) return { description: message }
}

const validatePatronSaint = (patronSaintName, t) => {
    if(!patronSaintName) return null;
    const rules = [
        lengthBetween(2, 180, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(patronSaintName, rules)
    if (message) return { patron_saint: message }
}

const validate = (t) => (values) => {
    const {
        title,
        description,
        department_group_title,
        patron_saint
    } = values ?? {}
    const errorTitle = validateTitleOrDepartmentGroupTitle(title, 'title', t)
    const errorDepartmentGroupTitle = validateTitleOrDepartmentGroupTitle(department_group_title, 'department_group_title', t)
    const errorPatronSaint = validatePatronSaint(patron_saint ? patron_saint.value ?? patron_saint.common_name : undefined, t)
    const errorDescription = validateDescription(description, t)
    return {
        ...errorTitle,
        ...errorDepartmentGroupTitle,
        ...errorPatronSaint,
        ...errorDescription,
    }
}

export const OrganizationDepartmentCreateForm = ({
                                                     organization,
                                                     initialValues,
                                                     onSubmit = NullAsDefault,
                                                     isSubmitting = false,
                                                 }) => {
    const t = useTranslate()

    const status_options = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]

    const _initialValues = useMemo(
        () => ({
            title: undefined,
            description: undefined,
            active: 1, status: 1,
            organization,
            organization_id: organization?.id,
            ...(initialValues ?? {}),
            status_options: status_options.map(deepTranslate(t)),
        }),
        [organization, initialValues]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const {
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-3"
                action={
                    <div className="flex items-center justify-end p-2 background">
                        <ResetButton disabled={isSubmitting}/>
                        <SubmitButton
                            label="create"
                            dirtyRequired={false}
                            loading={isSubmitting}
                        />
                    </div>
                }>
                <FieldsFactory
                    formSchema={[
                        formSchema.department_group,
                        formSchema.title,
                        formSchema.description,
                        formSchema.patron_saint,
                    ]}
                />

                <div className="flex flex-col gap-3">
                    <HeaderWithIconLine
                        className="pt-4 mt-3"
                        renderTitle={() => (
                            <div className="flex items-center gap-2">
                                <BsShieldFillExclamation size={16} className="text-color-500"/>
                                <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                        {t('privacy')}
                                    </span>
                            </div>
                        )}
                    />
                    <div className="ml-2 flex flex-col gap-3">
                        <FieldsFactory
                            formSchema={[
                                // formSchema.status,
                                formSchema.active,
                                formSchema.privacy,
                                formSchema.channel_post_permission,
                            ]}
                        />
                    </div>
                </div>

            </ActionModalWrapper>
        </Formik>
    )
}

export const OrganizationDepartmentUpdateForm = ({
                                                     organization,
                                                     initialValues,
                                                     onSubmit = NullAsDefault,
                                                     isSubmitting = false,
                                                 }) => {
    const t = useTranslate()

    const status_options = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]
    const organization_id = getId(organization)
    const _initialValues = useMemo(() => {
        const {about, ...rest} = initialValues ?? {}
        return {
            organization_id,
            ...rest,
            status_options: status_options.map(deepTranslate(t)),
            about: convertFromString(about),
            patron_saint_id: _.get(rest, 'patron_saint.id', ''),
        }
    }, [organization_id, initialValues]);

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-2"
                action={
                    <div className="flex items-center justify-end p-2 background">
                        <ResetButton disabled={isSubmitting}/>
                        <SubmitButton
                            label="save"
                            dirtyRequired={false}
                            loading={isSubmitting}
                        />
                    </div>
                }>
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            formSchema.department_group,
                            formSchema.title,
                            formSchema.description,
                            formSchema.patron_saint,
                            formSchema.about,
                        ]}
                    />
                </Pure>

                <div className="mt-2 flex flex-col gap-3">
                    <HeaderWithIconLine
                        renderTitle={() => (
                            <div className="flex items-center gap-2">
                                <BsShieldFillExclamation size={16} className="text-color-500"/>
                                <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                    {t('status')}
                                </span>
                            </div>
                        )}
                    />
                    <div className="ml-2 flex flex-col gap-3">
                        <FieldsFactory
                            formSchema={[
                                // formSchema.status,
                                formSchema.active,
                            ]}
                        />
                    </div>
                </div>

                <div className="mt-2 flex flex-col gap-3">
                    <HeaderWithIconLine
                        renderTitle={() => (
                            <div className="flex items-center gap-2">
                                <BsShieldFillExclamation size={16} className="text-color-500"/>
                                <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                    {t('privacy')}
                                </span>
                            </div>
                        )}
                    />
                    <div className="ml-2 flex flex-col gap-3">
                        <FieldsFactory
                            formSchema={[
                                formSchema.privacy,
                                formSchema.channel_post_permission,
                            ]}
                        />
                    </div>
                </div>

            </ActionModalWrapper>
        </Formik>
    )
}

